import type { AxiosResponse } from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import type { ActionType } from 'typesafe-actions';

import type {
  OnboardingContract,
  OnboardingContractSignature,
} from '@evenfinancial/finance-client';

import { client } from '@portal/api-client/src/client';
import { getPortalError } from '@portal/common';

import { selectEngineUser } from '../user/selectors';

import * as actions from './actions';

export function* getAllOnboardingContracts() {
  try {
    const { data: response }: AxiosResponse<OnboardingContract[]> = yield call(
      [client, 'get'],
      '/onboarding_contract'
    );

    yield put(actions.getAllOnboardingContractsAction.success(response));
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.getAllOnboardingContractsAction.failure(portalError));
  }
}

export function* getSignedOnboardingContracts() {
  try {
    const { data: response }: AxiosResponse<OnboardingContractSignature[]> =
      yield call([client, 'get'], '/onboarding_contract/signed');

    yield put(actions.getSignedOnboardingContractsAction.success(response));
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.getSignedOnboardingContractsAction.failure(portalError));
  }
}

export function* signOnboardingContracts(
  action: ActionType<typeof actions.signOnboardingContractsAction.request>
) {
  try {
    const { id: userId } = yield select(selectEngineUser);
    const onboardingContractTypes = action.payload.types;

    for (const type of onboardingContractTypes) {
      const { data: signature }: AxiosResponse<OnboardingContractSignature> =
        yield call([client, 'post'], '/onboarding_contract/sign', {
          type,
          userId,
        });

      yield put(actions.signOnboardingContractsAction.success(signature));

      // Re-request onboarding contracts
      yield put(actions.getAllOnboardingContractsAction.request({}));
      yield put(actions.getSignedOnboardingContractsAction.request({}));
    }
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.signOnboardingContractsAction.failure(portalError));
  }
}

export function* onboardingContractSaga() {
  yield takeLatest(
    getType(actions.getAllOnboardingContractsAction.request),
    getAllOnboardingContracts
  );

  yield takeLatest(
    getType(actions.getSignedOnboardingContractsAction.request),
    getSignedOnboardingContracts
  );

  yield takeEvery(
    getType(actions.signOnboardingContractsAction.request),
    signOnboardingContracts
  );
}
